import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConstants } from 'src/app/commons/app-constants';
import { DetailCacheService } from 'src/app/offline/detail-cache.service';
import {
  AdjustmentRequest,
  InventoryMovement,
  LotSizeLine,
  StockOutLine,
  InProgressSummary,
  InventorySummary,
  PageResponse,
  ProcessingRun,
  ProcessingRunInputDto,
  StockOutPreview,
  StockOutHold,
  TuInventorySearch,
  MovementSummary,
} from '../model/inventory.model';

@Injectable({ providedIn: 'root' })
export class InventoryService {
  private base = `${AppConstants.API_URL}api/v1`;

  constructor(private http: HttpClient, private detailCacheService: DetailCacheService) {}

  getSummary(): Observable<InventorySummary> {
    return this.http.get<InventorySummary>(`${this.base}/inventory/summary`);
  }

  receiveTransportUnit(transportUnitId: number): Observable<any> {
    return this.http.post(`${this.base}/inventory/receive/${transportUnitId}`, {});
  }

  /** Real, permanent stock debit for a Stock-Out Transport Unit. Independent of Shipment
   *  assignment (which is optional and can happen much later, or never). */
  confirmStockOut(transportUnitId: number): Observable<any> {
    return this.http.post(`${this.base}/inventory/confirm-stock-out/${transportUnitId}`, {});
  }

  /** Read-only preview of what confirmStockOut would debit — one line per distinct product on
   *  the TU (always one for Standard Layout, possibly several for Flexible Layout). Call before
   *  confirmStockOut so the user sees the breakdown first. */
  /** 2026-09-29: rebuilds this unconfirmed Stock Out's holds with the current rules (exact size,
   *  whole pieces, origin rule). Called when the Stock Out is opened; a no-op for anything else. */
  refreshStockOutHolds(transportUnitId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/inventory/stock-out/${transportUnitId}/refresh-holds`, {});
  }

  /** The company's "Let sales continue past available stock" setting (Application Settings → Inventory
   *  Policy). On a failed read this is null — callers must treat that as "unknown", not as OFF. */
  getAllowStockOversell(): Observable<boolean | null> {
    return this.http.get<{ allowStockOversell?: boolean }>(`${AppConstants.API_URL}api/v1/company-details`)
      .pipe(map(r => !!r?.allowStockOversell), catchError(() => of(null)));
  }

  /** Open Stock Outs that are holding stock, most idle first. */
  getStockOutHolds(): Observable<StockOutHold[]> {
    return this.http.get<StockOutHold[]>(`${this.base}/inventory/stock-out-holds`);
  }

  /** One-click release of an open Stock Out's holds (its rows stay; it holds again when opened or saved). */
  releaseStockOutHolds(transportUnitId: number): Observable<{ released: number }> {
    return this.http.post<{ released: number }>(`${this.base}/inventory/stock-out/${transportUnitId}/release-holds`, {});
  }

  previewStockOut(transportUnitId: number): Observable<StockOutPreview> {
    return this.http.get<StockOutPreview>(`${this.base}/inventory/confirm-stock-out/${transportUnitId}/preview`);
  }

  getMovements(filters: {
    movementType?: string;
    siteId?: number;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
    /** 2026-09-28 smart search: only rows about the picked Stock Unit / batch / job / consignment. */
    item?: { kind: string; encodedId: string } | null;
  }): Observable<PageResponse<InventoryMovement>> {
    let params = new HttpParams();
    if (filters.item?.kind && filters.item?.encodedId) {
      params = params.set('itemKind', filters.item.kind).set('itemId', filters.item.encodedId);
    }
    if (filters.movementType) params = params.set('movementType', filters.movementType);
    if (filters.siteId)       params = params.set('siteId', filters.siteId.toString());
    if (filters.from)         params = params.set('from', filters.from);
    if (filters.to)           params = params.set('to', filters.to);
    params = params.set('page', (filters.page ?? 0).toString());
    params = params.set('size', (filters.size ?? 50).toString());
    return this.http.get<PageResponse<InventoryMovement>>(`${this.base}/inventory/movements`, { params });
  }

  /** In/Out totals for the same filters as getMovements (2026-10-06). */
  getMovementSummary(filters: {
    movementType?: string;
    siteId?: number;
    from?: string;
    to?: string;
    item?: { kind: string; encodedId: string } | null;
  }): Observable<MovementSummary> {
    let params = new HttpParams();
    if (filters.item?.kind && filters.item?.encodedId) {
      params = params.set('itemKind', filters.item.kind).set('itemId', filters.item.encodedId);
    }
    if (filters.movementType) params = params.set('movementType', filters.movementType);
    if (filters.siteId)       params = params.set('siteId', filters.siteId.toString());
    if (filters.from)         params = params.set('from', filters.from);
    if (filters.to)           params = params.set('to', filters.to);
    return this.http.get<MovementSummary>(`${this.base}/inventory/movements/summary`, { params });
  }

  /** 2026-09-15 — In/Out Ledger Excel export, same filters as getMovements (times in the user's profile timezone). */
  exportMovements(filters: { movementType?: string; siteId?: number; from?: string; to?: string;
                             item?: { kind: string; encodedId: string } | null }): Observable<Blob> {
    let params = new HttpParams();
    // 2026-09-28: the export follows the smart-search box — exactly what is on screen.
    if (filters.item?.kind && filters.item?.encodedId) {
      params = params.set('itemKind', filters.item.kind).set('itemId', filters.item.encodedId);
    }
    if (filters.movementType) params = params.set('movementType', filters.movementType);
    if (filters.siteId)       params = params.set('siteId', filters.siteId.toString());
    if (filters.from)         params = params.set('from', filters.from);
    if (filters.to)           params = params.set('to', filters.to);
    return this.http.get(`${this.base}/movements/export`, { params, responseType: 'blob' });
  }

  getMovementsForTu(transportUnitId: number): Observable<InventoryMovement[]> {
    return this.detailCacheService.cacheThrough(
      `tuInventoryMovements:${transportUnitId}`,
      () => this.http.get<InventoryMovement[]>(`${this.base}/inventory/movements/tu/${transportUnitId}`),
    );
  }

  searchTusInInventory(query: string): Observable<TuInventorySearch[]> {
    return this.http.get<TuInventorySearch[]>(`${this.base}/inventory/tu-search?q=${encodeURIComponent(query)}`);
  }

  recordAdjustment(dto: AdjustmentRequest): Observable<any> {
    return this.http.post(`${this.base}/inventory/adjustments`, dto);
  }

  /** Sizes a batch really holds now (net) — for Add Adjustment. */
  lotSizes(lotEncodedId: string): Observable<LotSizeLine[]> {
    return this.http.get<LotSizeLine[]>(`${this.base}/inventory/lots/${encodeURIComponent(lotEncodedId)}/sizes`);
  }

  /** What a Stock Out unit took, one line per batch and size — for Add Adjustment. */
  stockOutLines(transportUnitId: number): Observable<StockOutLine[]> {
    return this.http.get<StockOutLine[]>(`${this.base}/inventory/stock-out/${transportUnitId}/lines`);
  }

  // Processing Runs
  /** @param status Comma-separated ProcessingRunStatus names (e.g. "COMPLETED,CANCELLED" for a
   *  "Finished" filter), or undefined for every status — 2026-09-02.
   *  @param sort 'newest' (default) | 'oldest' | 'code'. */
  listProcessingRuns(page = 0, size = 20, status?: string, sort: 'newest' | 'oldest' | 'code' = 'newest',
                     item?: { kind: string; encodedId: string } | null): Observable<PageResponse<ProcessingRun>> {
    let params = new HttpParams().set('page', page).set('size', size).set('sort', sort);
    if (status) params = params.set('status', status);
    // 2026-09-28 smart search: only jobs about the picked Stock Unit / batch / job / consignment.
    if (item?.kind && item?.encodedId) params = params.set('itemKind', item.kind).set('itemId', item.encodedId);
    return this.http.get<PageResponse<ProcessingRun>>(`${this.base}/processing-runs`, { params });
  }

  /** 2026-09-17 — runs assigned to the operator linked to MY login ("My Tasks" strip). */
  listMyProcessingRuns(status: string, size = 20): Observable<PageResponse<ProcessingRun>> {
    const params = new HttpParams().set('mine', true).set('status', status).set('page', 0).set('size', size);
    return this.http.get<PageResponse<ProcessingRun>>(`${this.base}/processing-runs`, { params });
  }

  getProcessingRun(encodedId: string): Observable<ProcessingRun> {
    return this.http.get<ProcessingRun>(`${this.base}/processing-runs/${encodedId}`);
  }

  createProcessingRun(data: Partial<ProcessingRun>): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs`, data);
  }

  /** Mill task Accept (2026-09-17) — only the assigned operator's own login; the server checks it. */
  acceptProcessingRun(encodedId: string): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/accept`, {});
  }

  /** "Can't do it" (2026-09-17) - the assigned operator hands a not-started task back. */
  declineProcessingRun(encodedId: string, reason: string): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/decline`, { reason });
  }

  startProcessingRun(encodedId: string, inputLotId?: number | null): Observable<ProcessingRun> {
    const query = inputLotId != null ? `?inputLotId=${inputLotId}` : '';
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/start${query}`, {});
  }

  completeProcessingRun(encodedId: string, output: Partial<ProcessingRun>): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/complete`, output);
  }

  cancelProcessingRun(encodedId: string): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/cancel`, {});
  }

  /** 2026-10-02: the requester takes a waiting Custom-Made request back (the row's small "x"); the normal cancel above is for mill staff. */
  withdrawCustomMadeRequest(encodedId: string): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/withdraw`, {});
  }

  /** Pause/Resume (2026-09-02) — see ProcessingRunStatus.PAUSED's own backend javadoc. reason is
   *  always optional, never required. */
  pauseProcessingRun(encodedId: string, reason?: string): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/pause`, { reason });
  }

  resumeProcessingRun(encodedId: string): Observable<ProcessingRun> {
    return this.http.post<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/resume`, {});
  }

  editProcessingRun(encodedId: string, output: Partial<ProcessingRun>): Observable<ProcessingRun> {
    return this.http.patch<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/output`, output);
  }

  /** Job nickname (2026-09-04) — own tiny endpoint, same reasoning as InventoryLotService's
   *  renameLot(). Blank clears the nickname back to "unnamed." */
  renameProcessingRun(encodedId: string, name: string): Observable<ProcessingRun> {
    return this.http.patch<ProcessingRun>(`${this.base}/processing-runs/${encodedId}/name`, { name });
  }

  searchTallySheets(q: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/processing-runs/tally-search?q=${encodeURIComponent(q)}`);
  }

  // 2026-09-15: returns the tally id (a new job's output tally has no Stock Unit); transportUnitEncodedId
  // is only present for an older job that still has its PROC-OUT unit.
  createOutputTally(runEncodedId: string, summaryType: string): Observable<{ tallyMasterId: number; tallyType?: string; transportUnitEncodedId?: string; alreadyExists: boolean }> {
    return this.http.post<any>(`${this.base}/processing-runs/${runEncodedId}/output-tally`, { summaryType });
  }

  /** 2026-10-02: remembers the product / store picked in the Output dialog before the job is finished. */
  saveOutputDraft(runEncodedId: string, draft: { outputProductId: number | null; outputSiteId: number | null }): Observable<ProcessingRun> {
    return this.http.put<ProcessingRun>(`${this.base}/processing-runs/${runEncodedId}/output-draft`, draft);
  }

  /** 2026-10-02: the "At the mill" line - jobs still running, never counted as stock. */
  getInProgressSummary(): Observable<InProgressSummary> {
    return this.http.get<InProgressSummary>(`${this.base}/processing-runs/in-progress-summary`);
  }

  getOutputTallyTotals(runEncodedId: string): Observable<{ linked: boolean; totalCbm: number; rowCount: number; totalPieces: number; tallyType?: string; tuEncodedId?: string; tallyMasterId?: number; locked?: boolean }> {
    return this.http.get<any>(`${this.base}/processing-runs/${runEncodedId}/output-tally-totals`);
  }

  getActiveShipments(): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/shipments/list?page=0&size=100&sort=shipmentDate,desc&status=ACTIVE`);
  }

  getAvailableInputTus(): Observable<ProcessingRunInputDto[]> {
    return this.http.get<ProcessingRunInputDto[]>(`${this.base}/processing-runs/available-inputs`);
  }
}
